// VRG-162 — replacement for the production "Simulate success/error" buttons that were
// removed from apps/app's ScanTag.tsx (dev must render 1:1 with prod, so a dev-only
// control can't live inside the production component). This Labs tab exercises the
// scan-gated transfer steps (sender confirm / recipient accept) against a REAL
// certificate id, via a server endpoint that reuses ScanProcessorService directly —
// the same code path the real NFC scan hits — rather than faking the result client-side.
//
// Since VRG-138, claiming itself no longer requires a scan at all (the emailed
// claim-token IS the production claim flow), so this simulator's only remaining
// purpose is exercising the two steps that are STILL scan-gated: the sender
// confirming handoff (transfer_initiated → transfer_waiting_confirmation) and the
// recipient accepting by scan (waiting_accept → active). Labs runs as the internal
// admin while the scan it simulates happens as a customer in apps/app, so this acts
// on a certificate id typed in by the admin — never "my session".
//
// Double-gated server-side (non-production env + PlatformAdmin) — see labs.controller.ts.
import React, { useEffect, useState } from 'react'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { SmartphoneNfcIcon, SearchIcon, InboxIcon, ChevronRightIcon } from 'lucide-react'
import { Button, Input, Card, Skeleton } from '@repo/ui'

import { api } from 'services'
import { StatusChip } from 'components'
import { useSnackbar } from 'contexts'

interface ScanSimulatorState {
  certificateId: string
  certificateStatus: string
  transferStatus: string | null
  transferType: string | null
  currentOwner: { id: string; email: string; name: string } | null
  nextStep: 'sender_confirm' | 'recipient_accept' | null
  nextStepLabel: string | null
}

interface ScanSimulatorCandidate {
  certificateId: string
  certificateStatus: string
  productTitle: string | null
  ownerName: string | null
  ownerEmail: string | null
  nextStep: string | null
  nextStepLabel: string | null
}

/**
 * A single scan-gated candidate — one click loads it into the simulator
 * below, no UUID copy-paste required. Follows the platform's action-row
 * convention (working-conventions §34): rest border `primary[100]`, hover +
 * `:focus-visible` = brand border + ring (mirrors `AddModal`'s
 * `MethodOption`/`IntegrationRow`, which this list is closest in shape to).
 */
const CandidateRow = ({
  candidate,
  onClick,
}: {
  candidate: ScanSimulatorCandidate
  onClick: () => void
}) => {
  const theme = useTheme()
  const ownerLabel = candidate.ownerName
    ? candidate.ownerEmail
      ? `${candidate.ownerName} (${candidate.ownerEmail})`
      : candidate.ownerName
    : (candidate.ownerEmail ?? 'No owner on record')

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        p: 3,
        cursor: 'pointer',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.primary[100]}`,
        backgroundColor: theme.palette.background.paper,
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
        '&:hover, &:focus-visible': {
          borderColor: theme.palette.brand[600],
          boxShadow: `0 0 0 3px ${theme.palette.brand['600-6%']}`,
          outline: 'none',
        },
      }}
    >
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
          {candidate.productTitle ?? 'Untitled product'}
        </Typography>
        <Typography variant="body3" color="text.secondary" noWrap>
          {ownerLabel}
        </Typography>
      </Stack>
      <Stack spacing={0.5} alignItems="flex-end" sx={{ flexShrink: 0 }}>
        <StatusChip statuses={CERTIFICATE_STATUSES} status={candidate.certificateStatus} size="small" />
        <Typography variant="body3" color="text.secondary">
          {candidate.nextStepLabel ?? '—'}
        </Typography>
      </Stack>
      <ChevronRightIcon strokeWidth={1} size={18} color={theme.palette.text.secondary} />
    </Box>
  )
}

const CERTIFICATE_STATUSES: Record<string, { label: string; color: string; backgroundColor: string; borderColor: string }> = {
  created: { label: 'Created', color: '#64748b', backgroundColor: 'rgba(100,116,139,0.1)', borderColor: '#cbd5e1' },
  inactive: { label: 'Inactive', color: '#64748b', backgroundColor: 'rgba(100,116,139,0.1)', borderColor: '#cbd5e1' },
  active: { label: 'Active', color: '#16a34a', backgroundColor: 'rgba(22,163,74,0.1)', borderColor: '#86efac' },
  transfer_initiated: { label: 'Transfer initiated', color: '#f59e0b', backgroundColor: 'rgba(245,158,11,0.1)', borderColor: '#fcd34d' },
  transfer_waiting_confirmation: { label: 'Waiting confirmation', color: '#f59e0b', backgroundColor: 'rgba(245,158,11,0.1)', borderColor: '#fcd34d' },
  waiting_accept: { label: 'Waiting accept', color: '#f59e0b', backgroundColor: 'rgba(245,158,11,0.1)', borderColor: '#fcd34d' },
  cancel_request: { label: 'Cancel request', color: '#dc2626', backgroundColor: 'rgba(220,38,38,0.1)', borderColor: '#fca5a5' },
  lost: { label: 'Lost', color: '#dc2626', backgroundColor: 'rgba(220,38,38,0.1)', borderColor: '#fca5a5' },
  stolen: { label: 'Stolen', color: '#dc2626', backgroundColor: 'rgba(220,38,38,0.1)', borderColor: '#fca5a5' },
}

/**
 * Labs → Scan simulator: type in a certificate id, see its current
 * certificate/transfer status + current owner, and (when it's sitting at a
 * scan-gated step) advance that step exactly as a real NFC scan would.
 */
export const ScanSimulatorLab = () => {
  const { open } = useSnackbar()
  const [certificateId, setCertificateId] = useState('')
  const [state, setState] = useState<ScanSimulatorState | null>(null)
  const [loading, setLoading] = useState(false)
  const [stepping, setStepping] = useState(false)

  const [candidates, setCandidates] = useState<ScanSimulatorCandidate[]>([])
  const [candidatesLoading, setCandidatesLoading] = useState(true)

  const loadCandidates = async () => {
    setCandidatesLoading(true)
    try {
      const { data } = await api.get('/internal/labs/scan-simulator')
      setCandidates(data?.candidates ?? [])
    } catch {
      // Non-fatal — the manual id input below still works even if the candidate
      // list fails to load (e.g. a transient network blip).
      setCandidates([])
    } finally {
      setCandidatesLoading(false)
    }
  }

  useEffect(() => {
    loadCandidates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const load = async (idOverride?: string) => {
    const id = (idOverride ?? certificateId).trim()
    if (!id) return
    setLoading(true)
    setState(null)
    try {
      const { data } = await api.get(`/internal/labs/scan-simulator/${id}`)
      setState(data)
    } catch (e: any) {
      open({ type: 'error', title: 'Could not load certificate', text: e?.response?.data?.message || 'Check the certificate id.' })
    } finally {
      setLoading(false)
    }
  }

  const selectCandidate = (candidate: ScanSimulatorCandidate) => {
    setCertificateId(candidate.certificateId)
    load(candidate.certificateId)
  }

  const advance = async () => {
    const id = certificateId.trim()
    if (!id) return
    setStepping(true)
    try {
      const { data } = await api.post(`/internal/labs/scan-simulator/${id}/step`)
      setState(data)
      open({ type: 'success', title: 'Scan simulated', text: 'Advanced the certificate past its scan-gated step.' })
      // The advance just moved this certificate off its scan-gated status (or to
      // the next one) — the candidate list would otherwise show a stale row that's
      // no longer actionable.
      loadCandidates()
    } catch (e: any) {
      open({ type: 'error', title: 'Could not simulate scan', text: e?.response?.data?.message || 'Something went wrong.' })
    } finally {
      setStepping(false)
    }
  }

  return (
    <Stack spacing={4} sx={{ maxWidth: 640 }}>
      <Stack spacing={1}>
        <Typography variant="h6">Scan simulator</Typography>
        <Typography variant="body2" color="text.secondary">
          Advances a certificate through its scan-gated transfer steps (sender confirm /
          recipient accept) without NFC hardware, by calling the same{' '}
          <code>ScanProcessorService</code> methods a real tag scan hits. Since claiming no
          longer requires a scan (the emailed claim link is the production flow), this only
          matters for the two steps that are still scan-gated.
        </Typography>
      </Stack>

      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2">Waiting on a scan</Typography>
          <Typography variant="body3" color="text.secondary">
            The only certificates the simulator can act on right now — click one to load it below.
          </Typography>
        </Stack>

        {candidatesLoading ? (
          <Stack spacing={2}>
            <Skeleton height={64} />
            <Skeleton height={64} />
          </Stack>
        ) : candidates.length === 0 ? (
          <Card sx={{ p: 4 }}>
            <Stack spacing={1} alignItems="center" sx={{ textAlign: 'center' }}>
              <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                <InboxIcon strokeWidth={1} size={24} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Nothing is waiting on a scan right now
              </Typography>
              <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5, maxWidth: 420 }}>
                To create one, open <code>apps/app</code> as the owner of a certificate and start a
                gift transfer — it lands here as soon as it's <code>transfer_initiated</code>. A
                certificate mid-claim (<code>waiting_accept</code>) shows up here too.
              </Typography>
            </Stack>
          </Card>
        ) : (
          <Stack spacing={2}>
            {candidates.map(candidate => (
              <CandidateRow key={candidate.certificateId} candidate={candidate} onClick={() => selectCandidate(candidate)} />
            ))}
          </Stack>
        )}
      </Stack>

      <Stack direction="row" spacing={3} alignItems="flex-end">
        <Box sx={{ flex: 1 }}>
          <Input
            label="Certificate id"
            placeholder="e.g. 009027a3-f078-46e9-8018-eab3320413d6"
            value={certificateId}
            onValueChange={setCertificateId}
            startAdornment={<SearchIcon size={16} strokeWidth={1.5} />}
          />
        </Box>
        <Button variant="secondary" loading={loading} disabled={!certificateId.trim()} onClick={() => load()}>
          Load
        </Button>
      </Stack>

      {state && (
        <Card sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Certificate status
              </Typography>
              <StatusChip statuses={CERTIFICATE_STATUSES} status={state.certificateStatus} size="small" />
            </Stack>

            {state.transferStatus && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Transfer
                </Typography>
                <Typography variant="body2">
                  {state.transferType ? `${state.transferType} · ` : ''}
                  {state.transferStatus}
                </Typography>
              </Stack>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Current owner
              </Typography>
              <Typography variant="body2">
                {state.currentOwner ? `${state.currentOwner.name} (${state.currentOwner.email})` : '—'}
              </Typography>
            </Stack>

            <Box sx={{ borderTop: theme => `1px solid ${theme.palette.divider}`, pt: 3 }}>
              {state.nextStep ? (
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Next scan-gated step: <strong>{state.nextStepLabel}</strong>
                  </Typography>
                  <Button
                    variant="primary"
                    startIcon={<SmartphoneNfcIcon size={16} strokeWidth={1.5} />}
                    loading={stepping}
                    onClick={advance}
                  >
                    Simulate scan
                  </Button>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  This certificate isn't waiting on a scan-gated step right now.
                </Typography>
              )}
            </Box>
          </Stack>
        </Card>
      )}
    </Stack>
  )
}
